import React, { useCallback } from 'react';

import {
  Button,
  ListButton,
  TopToolbar,
  useNotify,
  useRecordContext,
  useRedirect,
  useResourceContext,
} from 'react-admin';

import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import FileCopyIcon from '@mui/icons-material/FileCopy';
import StarIcon from '@mui/icons-material/Star';
import { pluginsDataProvider } from '@src/providers/dataProvider/resources';
import { PluginResource } from '@src/types';
import { PluginConfigVersion } from '@troverecommerce/ps-configs-api-client';

const PluginEditActions = () => {
  const record = useRecordContext<{ configVersion: PluginConfigVersion }>();
  const redirect = useRedirect();
  const resource = useResourceContext() as PluginResource;
  const notify = useNotify();

  const configVersion = record?.configVersion;
  const id = `${configVersion?.id}`;

  const redirectToList = useCallback(() => {
    redirect(`/${resource}`);
  }, [redirect, resource]);

  const handleCopyId = useCallback(async () => {
    if (!configVersion) return;
    try {
      await navigator.clipboard.writeText(id);
      notify('ID copied to clipboard', { type: 'success' });
    } catch (error) {
      notify('Failed to copy ID', { type: 'error' });
    }
  }, [configVersion, id, notify]);

  const handleSetAsDefault = useCallback(async () => {
    if (!configVersion) return;
    try {
      await pluginsDataProvider.setDefaultConfigVersion(resource, {
        data: { id: id },
      });
      notify('Configuration set as default successfully', { type: 'success' });
      redirectToList();
    } catch (error) {
      notify('Failed to set configuration as default', { type: 'error' });
    }
  }, [resource, configVersion, id, notify, redirectToList]);

  const handleDuplicate = useCallback(async () => {
    if (!configVersion) return;
    try {
      await pluginsDataProvider.duplicateConfigVersion(resource, {
        data: {
          name: `${configVersion.name} (Copy)`,
          sourceVersion: id,
        },
      });
      notify('Configuration duplicated successfully', { type: 'success' });
      redirectToList();
    } catch (error) {
      notify('Failed to duplicate configuration', { type: 'error' });
    }
  }, [resource, configVersion, id, notify, redirectToList]);

  return (
    <TopToolbar>
      <ListButton />
      <Button onClick={handleCopyId}>
        <ContentCopyIcon sx={{ mr: 1 }} />
        Copy ID to Clipboard
      </Button>
      <Button onClick={handleSetAsDefault} disabled={configVersion?.isDefault}>
        <StarIcon sx={{ mr: 1 }}/>
        Set as default
      </Button>
      <Button onClick={handleDuplicate}>
        <FileCopyIcon sx={{ mr: 1 }}/>
        Duplicate
      </Button>
    </TopToolbar>
  );
};

export default PluginEditActions;
